import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDownTrayIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n';
import { useOverlayDismiss } from '../components/dismiss-gesture';
import { buildReport } from './envelope';

/**
 * Modale « Signaler un bug ». CONTEXT-FREE hors i18n (n'utilise ni `useAuth` ni le
 * store applicatif) → utilisable depuis un `ErrorBoundary`, y compris après un
 * crash de l'arbre applicatif.
 *
 * L'enveloppe est assemblée AU CLIC (via `buildReport`), jamais en passif. Le
 * `reportId` et l'instant de capture sont figés à l'ouverture (stables pendant
 * l'édition de la description). L'utilisateur COPIE ou TÉLÉCHARGE — aucune requête
 * réseau.
 */
interface BugReportModalProps {
  onClose: () => void;
}

export function BugReportModal({ onClose }: BugReportModalProps): React.JSX.Element {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<Element | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overlayDismiss = useOverlayDismiss(onClose);

  // Figés à l'ouverture : l'identité et l'instant du rapport ne bougent pas quand
  // l'utilisateur tape sa description.
  const reportIdRef = useRef<string | undefined>(undefined);
  const capturedAtRef = useRef<Date | undefined>(undefined);
  if (reportIdRef.current === undefined) {
    const initial = buildReport('');
    reportIdRef.current = initial.reportId;
    capturedAtRef.current = initial.envelope.time ? new Date(initial.envelope.time) : new Date();
  }

  const [description, setDescription] = useState('');
  const [copied, setCopied] = useState(false);

  const report = useMemo(() => {
    const reportId = reportIdRef.current!;
    const capturedAt = capturedAtRef.current!;
    return buildReport(description, { reportId, capturedAt });
  }, [description]);

  // Verrou de défilement + focus initial (description) + restauration à la fermeture.
  useEffect(() => {
    restoreFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    textareaRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (restoreFocusRef.current instanceof HTMLElement) restoreFocusRef.current.focus();
    };
  }, []);

  // Échap ferme.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(report.text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Presse-papier bloqué (permission/contexte non sécurisé) : échec silencieux,
      // l'utilisateur peut toujours sélectionner le texte ou télécharger.
    }
  }, [report.text]);

  const onDownload = useCallback(() => {
    const blob = new Blob([report.json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.reportId}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [report.json, report.reportId]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      {...overlayDismiss}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('bugReport.title')}
        className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="text-lg font-semibold text-fg">{t('bugReport.title')}</h2>
          <button
            type="button"
            onClick={onClose}
            title={t('bugReport.close')}
            aria-label={t('bugReport.close')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Corps défilant. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          <p className="text-sm text-muted">{t('bugReport.intro')}</p>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-fg">{t('bugReport.descriptionLabel')}</span>
            <textarea
              ref={textareaRef}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('bugReport.descriptionPlaceholder')}
              rows={3}
              className="w-full resize-y rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-fg">
              {t('bugReport.technicalLabel')} — {report.reportId}
            </span>
            <textarea
              readOnly
              value={report.text}
              rows={10}
              aria-label={t('bugReport.technicalLabel')}
              className="w-full resize-y rounded-md border border-border bg-surface-2 px-3 py-2 font-mono text-xs text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            />
          </label>
        </div>

        {/* Actions. Note anti-secret à gauche (comme la référence), boutons à droite. */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border p-4">
          <p className="text-xs text-muted">{t('bugReport.noPasswords')}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onDownload}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
              {t('bugReport.download')}
            </button>
            <button
              type="button"
              onClick={() => void onCopy()}
              className="inline-flex items-center gap-2 rounded-md bg-brand-strong px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {copied ? (
                <CheckIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ClipboardDocumentIcon className="h-5 w-5" aria-hidden="true" />
              )}
              {copied ? t('bugReport.copied') : t('bugReport.copy')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
